import {
  Component,
  ChangeDetectionStrategy,
  inject,
  input,
  model,
  output,
  signal,
  computed,
  effect,
} from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { SolicitudVacaciones, SaldoVacaciones } from '@schemas/vacaciones.schema';
import { VacacionesService } from '@services/vacaciones.service';
import { describirAdscripcion } from '@shared/adscripcion';
import { incumplePoliticaAnticipacion } from '@shared/vacaciones.helpers';

export type AprobarModalPaso = 'director' | 'rh';
export type AprobarModalAccion = 'aprobar' | 'rechazar';

import { DetailField } from '../detail-field/detail-field';
import { PageHeader, SectionHeader } from '@intelishs-group/ui';
import { leerSaldo } from '@shared/vacaciones.utils';

@Component({
  selector: 'app-solicitud-aprobar-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DetailField,
    PageHeader,
    SectionHeader,
    DatePipe,
    DecimalPipe,
    FormsModule,
    NzButtonModule,
    NzModalModule,
    NzInputModule,
    NzIconModule,
  ],
  templateUrl: './solicitud-aprobar-modal.html',
  styleUrl: './solicitud-aprobar-modal.css',
})
export class SolicitudAprobarModal {
  private readonly vacacionesService = inject(VacacionesService);
  private readonly message = inject(NzMessageService);

  visible = model(false);
  solicitud = input<SolicitudVacaciones | null>(null);
  accion = input<AprobarModalAccion>('aprobar');
  paso = input<AprobarModalPaso>('director');
  procesado = output<void>();

  comentarios = signal('');
  procesando = signal(false);
  saldo = signal<SaldoVacaciones | null>(null);
  loadingSaldo = signal(false);

  describirAdscripcion = describirAdscripcion;

  incumplePolitica = computed(() => {
    const sol = this.solicitud();
    if (!sol) return false;
    return incumplePoliticaAnticipacion(sol.fechaSolicitud, sol.fechaInicio);
  });

  /** Proyección de saldo si se aprueba la solicitud seleccionada. */
  saldoProyeccion = computed(() => {
    const saldo = this.saldo();
    const sol = this.solicitud();
    if (!saldo || !sol || this.accion() !== 'aprobar') return null;
    const disponiblesSinEsta = saldo.diasDisponiblesTotal + sol.diasSolicitados;
    const saldoFinal = saldo.diasDisponiblesTotal;
    // El saldo ya cuenta esta solicitud (las pendientes consumen). Un total
    // negativo a hoy NO es deuda si los días se cubren a su fecha: eso lo dice
    // `tieneSaldoNegativo`. Antes se derivaba de `saldoFinal < 0` y pedía
    // "aprobar de todas formas" cualquier solicitud a futuro.
    const lectura = leerSaldo(saldo);
    return {
      disponiblesSinEsta,
      diasSolicitados: sol.diasSolicitados,
      saldoFinal,
      esNegativo: lectura.tipo === 'deuda',
      diasDeuda: lectura.tipo === 'deuda' ? lectura.dias : 0,
      aFuturo: lectura.tipo === 'comprometido' ? lectura.aFuturo : 0,
    };
  });

  /** Va en el subtítulo, junto al ID: "MC0458 · Aprobar como director". */
  /**
   * El contexto va en el SUBTÍTULO, nunca en el título, y se arma aquí porque
   * `page-header` recibe un string: con partes opcionales, el encadenado se
   * lee peor en el template.
   */
  subtitulo = computed(() => {
    const sol = this.solicitud();
    return sol ? `${sol.empleadoIdInterno} · ${this.modalTitle()}` : '';
  });

  modalTitle = computed(() => {
    const accion = this.accion() === 'aprobar' ? 'Aprobar' : 'Rechazar';
    const por = this.paso() === 'director' ? 'como director' : 'como RH';
    return `${accion} ${por}`;
  });

  okText = computed(() => {
    if (this.accion() === 'aprobar') {
      return this.saldoProyeccion()?.esNegativo ? 'Aprobar de todas formas' : 'Aprobar';
    }
    return 'Rechazar';
  });

  okDanger = computed(
    () => this.accion() === 'rechazar' || (this.saldoProyeccion()?.esNegativo ?? false),
  );

  constructor() {
    effect(() => {
      if (!this.visible()) return;
      // Reset form state
      this.comentarios.set('');
      this.saldo.set(null);
      // Cargar saldo cuando se abre para aprobar
      const sol = this.solicitud();
      if (this.accion() === 'aprobar' && sol) {
        this.loadingSaldo.set(true);
        this.vacacionesService.getSaldo(sol.empleadoId).subscribe({
          next: (s) => {
            this.saldo.set(s);
            this.loadingSaldo.set(false);
          },
          error: () => this.loadingSaldo.set(false),
        });
      }
    });
  }

  cerrar(): void {
    this.visible.set(false);
  }

  confirmarAccion(): void {
    const sol = this.solicitud();
    if (!sol) return;
    this.procesando.set(true);

    const request = {
      comentarios: this.comentarios() || undefined,
    };
    const paso = this.paso();
    const accion = this.accion();

    let obs;
    if (paso === 'director') {
      obs =
        accion === 'aprobar'
          ? this.vacacionesService.aprobarDirector(sol.id, request)
          : this.vacacionesService.rechazarDirector(sol.id, request);
    } else {
      obs =
        accion === 'aprobar'
          ? this.vacacionesService.aprobarRH(sol.id, request)
          : this.vacacionesService.rechazarRH(sol.id, request);
    }

    obs.subscribe({
      next: () => {
        const label = accion === 'aprobar' ? 'aprobada' : 'rechazada';
        const por = paso === 'director' ? 'por Director' : 'por RH';
        // Para el flujo Director también se indica "pasa a revisión de RH"
        const suffix =
          paso === 'director' && accion === 'aprobar' ? ' — pasa a revisión de RH' : '';
        this.message.success(`Solicitud ${label} ${por}${suffix}`);
        this.procesando.set(false);
        this.procesado.emit();
        this.cerrar();
      },
      error: () => {
        this.message.error('Error al procesar la solicitud');
        this.procesando.set(false);
      },
    });
  }
}
